import {castDraft, produce} from 'immer';

import type {AgentChatAction} from './actions';
import {applyRuntimeEventToThread} from './eventReducer';
import {addMessageNode, switchBranch} from './messageTree';
import type {AgentChatState} from './thread';
import {createDefaultState, createEmptyThread} from './thread';

export function agentChatReducer(
  state: AgentChatState = createDefaultState(),
  action: AgentChatAction
): AgentChatState {
  return produce(state, draft => {
    switch (action.type) {
      case 'createThread': {
        const thread = createEmptyThread(action.id, action.timestamp);
        // NOTE: Use 'castDraft' to avoid "excessively deep" errors with
        // 'WritableDraft' inference (due to 'ReadonlyJSONValue').
        draft.threads[action.id] = castDraft(thread);
        draft.threadOrder.push(action.id);
        draft.activeThreadId = action.id;
        break;
      }

      case 'deleteThread': {
        delete draft.threads[action.threadId];
        draft.threadOrder = draft.threadOrder.filter(
          id => id !== action.threadId
        );
        if (draft.activeThreadId === action.threadId) {
          draft.activeThreadId = draft.threadOrder[0] ?? null;
        }
        break;
      }

      case 'setActiveThread': {
        if (draft.threads[action.threadId]) {
          draft.activeThreadId = action.threadId;
        }
        break;
      }

      case 'renameThread': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          thread.title = action.title;
          thread.updatedAt = action.timestamp;
        }
        break;
      }

      case 'addUserMessage': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          addMessageNode(thread.messageTree, action.parentId, action.message);
          thread.updatedAt = action.timestamp;
        }
        break;
      }

      case 'switchBranch': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          switchBranch(thread.messageTree, action.targetNodeId);
          thread.updatedAt = action.timestamp;
        }
        break;
      }

      case 'applyRuntimeEvent': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          applyRuntimeEventToThread(thread, action.event, action.timestamp);
        }
        break;
      }

      case 'setThreadStatus': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          thread.status = action.status;
          thread.updatedAt = action.timestamp;
        }
        break;
      }

      case 'setThreadError': {
        const thread = draft.threads[action.threadId];
        if (thread) {
          thread.error = action.error;
          thread.updatedAt = action.timestamp;
        }
        break;
      }
    }
  });
}
